import { createContext, useContext } from "react";

export type ToastTone = "neutral" | "positive" | "warning" | "critical";

export interface Toast {
  id: number;
  title: string;
  /** What to do about it. An error without this is just bad news. */
  hint?: string;
  tone: ToastTone;
  /** Where it appears. The default is the bottom right; "top" sits under the header, where a result of the page's own is read. */
  placement?: "top" | "bottom";
}

export interface ToastContextValue {
  toasts: Toast[];
  push: (toast: Omit<Toast, "id">) => void;
  dismiss: (id: number) => void;
}

/**
 * Lives apart from the components on purpose: a file that exports both
 * components and non-components breaks React Fast Refresh, so every toast
 * change would reload the whole app instead of hot-swapping.
 */
export const ToastContext = createContext<ToastContextValue | null>(null);

/** Raise a toast from anywhere inside the provider. */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside a ToastProvider");
  return ctx;
}
